// Textos de estado y motivos en lenguaje del panel. Los códigos vienen del backend (contratos,
// send-policy y trabajador); un código desconocido se muestra tal cual, nunca se inventa un significado.
import type { Channel, ConversationState, MessageKind, MessageStatus, Referral } from '@hilo/contracts';
import { ApiError, NetworkError, UNREACHABLE } from './api';

export const CHANNEL_LABEL: Record<Channel, string> = {
  WHATSAPP: 'WhatsApp',
  MESSENGER: 'Messenger',
  INSTAGRAM: 'Instagram',
};

export const STATE_LABEL: Record<ConversationState, string> = {
  OPEN: 'Abierta',
  WAITING: 'En espera',
  RESOLVED: 'Resuelta',
};

/**
 * Estado de un mensaje saliente. ACCEPTED es el ✓ de WhatsApp: el canal lo recibió, no que llegó al
 * teléfono del cliente (eso es DELIVERED).
 */
export function statusLabel(status: MessageStatus, _channel: Channel): string {
  switch (status) {
    case 'QUEUED': return 'En cola';
    case 'SENDING': return 'Enviando';
    case 'ACCEPTED': return 'Enviado';
    case 'DELIVERED': return 'Entregado';
    case 'READ': return 'Leído';
    case 'FAILED': return 'No enviado';
    case 'UNKNOWN': return 'Resultado desconocido';
  }
}

export function statusDetail(status: MessageStatus, channel: Channel = 'WHATSAPP'): string | null {
  switch (status) {
    case 'ACCEPTED': return `${CHANNEL_LABEL[channel]} lo recibió; aún sin confirmación de entrega al cliente.`;
    case 'UNKNOWN': return 'No sabemos si llegó. Hilo no lo reenviará por su cuenta.';
    default: return null;
  }
}

export const KIND_LABEL: Record<MessageKind, string> = {
  TEXT: 'Texto',
  IMAGE: 'Imagen',
  AUDIO: 'Audio',
  VIDEO: 'Video',
  DOCUMENT: 'Documento',
  UNSUPPORTED: 'Contenido no compatible',
};

export const REFERRAL_LABEL: Record<Referral['kind'], string> = {
  AD: 'Anuncio',
  POST: 'Publicación',
  STORY: 'Historia',
  UNKNOWN: 'Origen sin tipo',
};

/** Motivos estables de SendPolicy.reason (adaptadores y MessagesService.policy). */
const POLICY_REASON: Record<string, string> = {
  WINDOW_CLOSED: 'Pasaron más de 24 horas desde el último mensaje del cliente. Solo puedes enviar una plantilla aprobada.',
  IDENTITY_PENDING_REVIEW: 'La identidad de este contacto está pendiente de revisión. No se puede responder hasta que un administrador la revise.',
  INSTAGRAM_RECIPIENT_UNVERIFIED: 'Este cliente no ha escrito por la conexión actual de Instagram. Podrás responderle cuando vuelva a escribir.',
  IDENTITY_RECONCILIATION_PENDING: 'Hay un cambio de identidad de este contacto en proceso. Espera unos minutos para responder.',
  IDENTITY_CHANGE_UNRESOLVED: 'Este contacto tiene un cambio de identidad sin resolver. Un administrador debe revisarlo antes de responder.',
  NO_ACTIVE_ADDRESS: 'Este contacto no tiene una dirección activa para responder.',
  RECIPIENT_NOT_ALLOWED: 'La conexión está en modo de pruebas y este contacto no está en la lista de destinatarios permitidos.',
  CREDENTIALS_MISSING: 'La conexión no tiene credenciales configuradas.',
  CONNECTION_DISABLED: 'La conexión está desactivada.',
  CONVERSATION_RESOLVED: 'La conversación está resuelta. Ábrela de nuevo para responder.',
  CONVERSATION_NOT_FOUND: 'La conversación ya no existe o no tienes acceso.',
  ASSIGNEE_INVALID: 'Esa persona ya no está activa en Hilo: elige otro responsable.',
  TEMPLATE_UNSUPPORTED: 'Este canal no admite plantillas.',
  TEMPLATE_NOT_APPROVED: 'La plantilla no está aprobada para esta conexión.',
  ATTACHMENTS_UNAVAILABLE: 'Los adjuntos todavía no están disponibles en Hilo.',
  POLICY_DENIED: 'Este canal no permite enviar ahora.',
  ASSIGNMENT_CHANGED: 'La conversación cambió de responsable antes del envío.',
  ACCESS_REVOKED: 'Perdiste el acceso a esta conexión antes del envío.',
  ADDRESS_CHANGED: 'La dirección del contacto cambió durante el envío.',
};

export function policyReason(reason: string | null): string | null {
  if (reason === null) return null;
  return POLICY_REASON[reason] ?? `El canal no permite enviar ahora (código ${reason}).`;
}

/** Código de error de un mensaje FAILED o UNKNOWN (trabajador o proveedor). */
export function messageErrorText(code: string | null, channel: Channel = 'WHATSAPP'): string | null {
  if (code === null) return null;
  if (code === 'BLOCKED_WINDOW_CLOSED' && channel !== 'WHATSAPP') {
    return 'Hilo lo detuvo antes de enviarlo: venció el plazo de respuesta desde el último mensaje del cliente. Espera a que vuelva a escribir.';
  }
  const blocked = code.startsWith('BLOCKED_') ? code.slice('BLOCKED_'.length) : null;
  if (blocked && POLICY_REASON[blocked]) return `Hilo lo detuvo antes de enviarlo: ${POLICY_REASON[blocked]}`;
  switch (code) {
    case 'LEASE_EXPIRED':
    case 'INTERRUPTED':
      return 'El envío se interrumpió a mitad; no sabemos si llegó.';
    case 'PROVIDER_DISABLED':
      return 'Los envíos reales están apagados en este entorno.';
    default:
      return `Código ${code}.`;
  }
}

/** Mensaje para un error de API en una acción. La API ya devuelve textos en español. */
export function errorText(error: unknown): string {
  if (error instanceof NetworkError) return 'No hay conexión con Hilo. Revisa tu red e inténtalo de nuevo.';
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'ALREADY_ASSIGNED': return 'Otra persona tomó esta conversación antes que tú.';
      case 'REVISION_CONFLICT': return 'La conversación cambió mientras la mirabas. Ya está actualizada: revísala y vuelve a intentarlo.';
      case 'INVALID_CREDENTIALS': return 'Correo o contraseña incorrectos.';
      case 'RATE_LIMITED': return 'Demasiados intentos. Espera unos minutos antes de volver a intentarlo.';
      case 'NOT_FOUND': return 'Ya no tienes acceso a esta conversación o no existe.';
      case UNREACHABLE: return 'Hilo no responde ahora mismo (puede estar reiniciándose). Inténtalo de nuevo en unos segundos.';
      case 'SERVICE_UNAVAILABLE': return 'Hilo no está disponible ahora mismo. Inténtalo de nuevo en unos segundos.';
      default: return error.message.endsWith('.') ? error.message : `${error.message}.`;
    }
  }
  return 'Algo falló en el panel. Recarga la página.';
}

/** Un resultado incierto: la petición pudo llegar al servidor aunque no veamos respuesta. */
export function isUncertain(error: unknown): boolean {
  return error instanceof NetworkError || (error instanceof ApiError && error.status >= 500);
}
